import MoreVertIcon from '@mui/icons-material/MoreVert';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Alert, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Pagination, Stack, TextField, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { ptBR } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { camposDaEstrategia, labelDoCampoIdAgendamento, montarIdentificadorAgendamento, todosCamposIdAgendamentoPreenchidos } from '../../helpers/estrategiaIdAgendamento';
import CadastrosAgendamentosService from '../../service/CadastrosAgendamentos';
import Empresa from '../../service/Empresa';
import EstrategiaIdAgendamentoService from '../../service/EstrategiaIdAgendamento';
import Fluxo from '../../service/Fluxo';
import queryClient from '../../service/query';
import { Cadastro, CadastroModelo, CampoCadastro, CampoCadastroRespondido, FluxoModel, ModeloAgendamento } from '../../types/fluxo/types';
import GenericAutoComplete from '../GenericAutoComplete';

const service = new CadastrosAgendamentosService();
const fluxoService = new Fluxo();
const empresaService = new Empresa();
const estrategiaIdAgendamentoService = new EstrategiaIdAgendamentoService();
type ValorCampo = string | boolean;
const CADASTROS_POR_PAGINA = 5;

function normalizarTexto(valor: unknown) {
  return String(valor ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
}

export function valorParaEnvio(campo: CampoCadastro, valor: ValorCampo | undefined): CampoCadastroRespondido['resposta'] {
  if (campo.tipo === 'boolean') return valor === true || valor === 'true';
  if (campo.tipo === 'number') return Number(valor);
  if (campo.tipo === 'multiple') return String(valor ?? '').split(',').map((item) => item.trim()).filter(Boolean);
  return String(valor ?? '');
}

function valoresDoCadastro(cadastro: Cadastro): Record<string, ValorCampo> {
  return cadastro.campos.reduce<Record<string, ValorCampo>>((valores, campo) => ({
    ...valores,
    [campo.nome]: typeof campo.resposta === 'boolean' ? campo.resposta : Array.isArray(campo.resposta) ? campo.resposta.join(', ') : String(campo.resposta),
  }), {});
}

export default function Cadastros() {
  const { t } = useTranslation('translation');
  const instrucaoDaRegra = (minimo: number, maximo: number | null) => {
    if (maximo === minimo) return t('fluxo.registrations.selectExactly', { count: minimo });
    if (maximo === null) return t('fluxo.registrations.selectAtLeast', { count: minimo });
    if (minimo === 0) return t('fluxo.registrations.selectAtMost', { count: maximo });
    return t('fluxo.registrations.selectBetween', { minimum: minimo, maximum: maximo });
  };
  const [modeloId, setModeloId] = useState('');
  const [nome, setNome] = useState('');
  const [valores, setValores] = useState<Record<string, ValorCampo>>({});
  const [pagina, setPagina] = useState(1);
  const [pesquisaCadastros, setPesquisaCadastros] = useState('');
  const [cadastroEmEdicao, setCadastroEmEdicao] = useState<Cadastro | null>(null);
  const [cadastroSendoDuplicado, setCadastroSendoDuplicado] = useState<Cadastro | null>(null);
  const [cadastroParaExcluir, setCadastroParaExcluir] = useState<Cadastro | null>(null);
  const [menuCadastro, setMenuCadastro] = useState<{ ancora: HTMLElement; cadastro: Cadastro } | null>(null);
  const [modalAgendamentoAberto, setModalAgendamentoAberto] = useState(false);
  const [modeloAgendamentoId, setModeloAgendamentoId] = useState('');
  const [cadastrosPorRegra, setCadastrosPorRegra] = useState<Record<string, string[]>>({});
  const [valoresIdentificadorAgendamento, setValoresIdentificadorAgendamento] = useState<Record<string, string>>({});
  const [fluxoId, setFluxoId] = useState('');
  const [unidadeId, setUnidadeId] = useState('');
  const [dataPrevista, setDataPrevista] = useState<Date | null>(null);
  const [dataMinimaAgendamento, setDataMinimaAgendamento] = useState<Date | null>(null);
  const modelos = useQuery({
    queryKey: ['CADASTRO_MODELOS'],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      return service.listarModelosCadastro(token);
    },
    refetchOnWindowFocus: false,
  });
  const modelo = useMemo(() => modelos.data?.find((item) => item.id === modeloId), [modelos.data, modeloId]);
  const cadastros = useQuery({
    queryKey: ['CADASTROS', modeloId],
    enabled: Boolean(modeloId),
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      return service.listarCadastros(token, modeloId);
    },
    refetchOnWindowFocus: false,
  });
  const modelosAgendamento = useQuery({
    queryKey: ['AGENDAMENTO_MODELOS'],
    enabled: modalAgendamentoAberto,
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      return service.listarModelosAgendamento(token);
    },
    refetchOnWindowFocus: false,
  });
  const estrategiaIdAgendamento = useQuery({
    queryKey: ['ESTRATEGIA_ID_AGENDAMENTO'],
    enabled: modalAgendamentoAberto,
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      return estrategiaIdAgendamentoService.obter(token.userEmpresa);
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
  const modeloAgendamento = modelosAgendamento.data?.find((item) => item.id === modeloAgendamentoId);
  const fluxos = useQuery({
    queryKey: ['FLUXO_MODELOS'],
    enabled: modalAgendamentoAberto,
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      const response = await fluxoService.getAll(token);
      if (!response?.data.success || !Array.isArray(response.data.data)) throw new Error(t('fluxo.registrations.loadFlowModelsError'));
      return response.data.data as FluxoModel[];
    },
    refetchOnWindowFocus: false,
  });
  const fluxo = fluxos.data?.find((item) => item.id === fluxoId);
  const checklistFluxoId = fluxo?.fluxo?.[0]?.checklistId;
  const versaoFluxo = fluxo?.versaoFluxo;
  const unidades = useQuery({
    queryKey: ['UNIDADES_FLUXO', checklistFluxoId],
    enabled: Boolean(modalAgendamentoAberto && checklistFluxoId),
    queryFn: async () => {
      const token = await AttToken();
      if (!token || !checklistFluxoId) throw new Error(t('fluxo.registrations.tokenOrChecklistNotFound'));
      return empresaService.getAllByFormId(token, checklistFluxoId);
    },
    refetchOnWindowFocus: false,
  });
  const regrasAgendamento = modeloAgendamento?.regrasCadastro ?? [];
  const cadastrosDasRegras = useQueries({
    queries: regrasAgendamento.map((regra) => ({
      queryKey: ['CADASTROS', regra.idModeloCadastro],
      queryFn: async () => {
        const token = await AttToken();
        if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
        return service.listarCadastros(token, regra.idModeloCadastro);
      },
      enabled: modalAgendamentoAberto,
      refetchOnWindowFocus: false,
    })),
  });
  const salvarCadastro = useMutation({
    mutationFn: async () => {
      if (!modelo) throw new Error(t('fluxo.registrations.selectRegistrationModel'));
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      const campos = modelo.campos.map((campo) => ({ nome: campo.nome, resposta: valorParaEnvio(campo, valores[campo.nome]) }));
      if (cadastroEmEdicao) return service.editarCadastro(token, cadastroEmEdicao.id, { idModelo: modelo.id, nome, campos });
      return service.criarCadastro(token, { idModelo: modelo.id, nome, campos });
    },
    onSuccess: () => {
      setNome('');
      setValores({});
      setCadastroEmEdicao(null);
      setCadastroSendoDuplicado(null);
      void queryClient.invalidateQueries({ queryKey: ['CADASTROS', modeloId] });
      toast.success(t('fluxo.registrations.registrationSaved'));
    },
    onError: () => toast.error(t('fluxo.registrations.saveRegistrationError')),
  });
  const excluirCadastro = useMutation({
    mutationFn: async (cadastro: Cadastro) => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      return service.excluirCadastro(token, cadastro.id);
    },
    onSuccess: () => {
      setCadastroParaExcluir(null);
      void queryClient.invalidateQueries({ queryKey: ['CADASTROS', modeloId] });
      toast.success(t('fluxo.registrations.registrationDeleted'));
    },
    onError: () => toast.error(t('fluxo.registrations.deleteRegistrationError')),
  });
  const criarAgendamento = useMutation({
    onMutate: () => ({ toastId: toast.loading(t('fluxo.agendando')) }),
    mutationFn: async () => {
      if (!modeloAgendamento) throw new Error(t('fluxo.registrations.selectSchedulingModel'));
      if (!checklistFluxoId) throw new Error(t('fluxo.registrations.selectFlowModelWithForm'));
      if (!dataPrevista || dataPrevista.getTime() < Date.now()) throw new Error(t('fluxo.registrations.createSchedulingError'));
      if (!estrategiaIdAgendamento.data || !todosCamposIdAgendamentoPreenchidos(estrategiaIdAgendamento.data, valoresIdentificadorAgendamento)) throw new Error(t('fluxo.registrations.loadSchedulingStrategyError'));
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.registrations.tokenNotFound'));
      const cadastrosAssociados = regrasAgendamento.flatMap((regra) => (cadastrosPorRegra[regra.id] ?? []).map((idCadastro) => ({
        idRegraCadastro: regra.id,
        idCadastro,
        idModeloCadastro: regra.idModeloCadastro,
      })));
      return service.criarAgendamento(token, {
        idModeloAgendamento: modeloAgendamento.id,
        identificador: montarIdentificadorAgendamento(estrategiaIdAgendamento.data, valoresIdentificadorAgendamento),
        cadastros: cadastrosAssociados,
        idFluxo: fluxoId,
        idChecklist: checklistFluxoId,
        ...(typeof versaoFluxo === 'number' ? { versaoFluxo } : {}),
        idUnidade: unidadeId,
        dataPrevista: dataPrevista?.getTime() as number,
        chaveOrigem: `web-${crypto.randomUUID()}`,
        payloadReferencia: {},
      });
    },
    onSuccess: (_data, _variables, context) => {
      if (context?.toastId) {
        toast.update(context.toastId, { render: t('fluxo.registrations.schedulingCreated'), type: 'success', isLoading: false, autoClose: 3000 });
      } else {
        toast.success(t('fluxo.registrations.schedulingCreated'));
      }
      void queryClient.invalidateQueries({ queryKey: ['AGENDAMENTOS'] });
      fecharModalAgendamento();
    },
    onError: (_error, _variables, context) => {
      if (context?.toastId) {
        toast.update(context.toastId, { render: t('fluxo.registrations.createSchedulingError'), type: 'error', isLoading: false, autoClose: 5000 });
      } else {
        toast.error(t('fluxo.registrations.createSchedulingError'));
      }
    },
  });
  const camposObrigatoriosPreenchidos = modelo?.campos.every((campo) => !campo.obrigatorio || String(valores[campo.nome] ?? '').trim()) ?? false;
  const campoIdentificador = modelo?.campos.find((campo) => campo.identificador);
  const valorIdentificadorOriginal = cadastroSendoDuplicado?.campos.find((campo) => campo.nome === campoIdentificador?.nome)?.resposta;
  const duplicacaoMantemIdentificador = Boolean(cadastroSendoDuplicado && campoIdentificador && normalizarTexto(valorIdentificadorOriginal) === normalizarTexto(valorParaEnvio(campoIdentificador, valores[campoIdentificador.nome])));
  const dataPrevistaInvalida = Boolean(dataPrevista && dataPrevista.getTime() < Date.now());
  const cadastrosFiltrados = useMemo(() => {
    const termo = normalizarTexto(pesquisaCadastros.trim());
    if (!termo) return cadastros.data ?? [];

    return (cadastros.data ?? []).filter((cadastro) => [
      cadastro.nome,
      cadastro.identificador,
      cadastro.identificadorNormalizado,
      ...cadastro.campos.map((campo) => campo.resposta),
    ].some((valor) => normalizarTexto(valor).includes(termo)));
  }, [cadastros.data, pesquisaCadastros]);
  const totalPaginas = Math.ceil(cadastrosFiltrados.length / CADASTROS_POR_PAGINA);
  const cadastrosDaPagina = cadastrosFiltrados.slice((pagina - 1) * CADASTROS_POR_PAGINA, pagina * CADASTROS_POR_PAGINA);
  const regrasAtendidas = regrasAgendamento.every((regra) => {
    const quantidade = cadastrosPorRegra[regra.id]?.length ?? 0;
    return quantidade >= regra.minimo && (regra.maximo === null || quantidade <= regra.maximo);
  });
  const camposIdentificadorAgendamento = estrategiaIdAgendamento.data ? camposDaEstrategia(estrategiaIdAgendamento.data) : [];
  const identificadorAgendamentoPreenchido = Boolean(estrategiaIdAgendamento.data && todosCamposIdAgendamentoPreenchidos(estrategiaIdAgendamento.data, valoresIdentificadorAgendamento));

  function selecionarModelo(id: string) {
    setModeloId(id);
    setNome('');
    setValores({});
    setCadastroEmEdicao(null);
    setCadastroSendoDuplicado(null);
    setPagina(1);
    setPesquisaCadastros('');
  }

  function abrirModalAgendamento() {
    const agora = new Date();
    setModalAgendamentoAberto(true);
    setModeloAgendamentoId('');
    setCadastrosPorRegra({});
    setValoresIdentificadorAgendamento({});
    setFluxoId('');
    setUnidadeId('');
    setDataPrevista(agora);
    setDataMinimaAgendamento(agora);
  }

  function fecharModalAgendamento() {
    setModalAgendamentoAberto(false);
    setModeloAgendamentoId('');
    setCadastrosPorRegra({});
    setValoresIdentificadorAgendamento({});
    setFluxoId('');
    setUnidadeId('');
    setDataPrevista(null);
    setDataMinimaAgendamento(null);
  }

  function selecionarModeloAgendamento(id: string) {
    setModeloAgendamentoId(id);
    setCadastrosPorRegra({});
  }

  function selecionarFluxo(id: string) {
    setFluxoId(id);
    setUnidadeId('');
  }

  function cancelarEdicao() {
    setNome('');
    setValores({});
    setCadastroEmEdicao(null);
    setCadastroSendoDuplicado(null);
  }

  function editarCadastro(cadastro: Cadastro) {
    setNome(cadastro.nome);
    setValores(valoresDoCadastro(cadastro));
    setCadastroEmEdicao(cadastro);
    setCadastroSendoDuplicado(null);
  }

  function duplicarCadastro(cadastro: Cadastro) {
    setNome(`${cadastro.nome} (cópia)`);
    setValores(valoresDoCadastro(cadastro));
    setCadastroEmEdicao(null);
    setCadastroSendoDuplicado(cadastro);
  }

  function fecharMenuCadastro() {
    setMenuCadastro(null);
  }

  return <Stack spacing={2}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Typography variant="h6" color="textSecondary">{t('fluxo.registrations.title')}</Typography>
      <Button variant="contained" startIcon={<CalendarMonthIcon />} onClick={abrirModalAgendamento}>{t('fluxo.flowScheduling')}</Button>
    </Stack>
    <Typography variant="body2" color="textSecondary">{t('fluxo.registrations.description')}</Typography>
    {modelos.isError && <Alert severity="error">{t('fluxo.registrations.loadRegistrationModelsError')}</Alert>}
    <TextField select label={t('fluxo.registrations.registrationModel')} value={modeloId} onChange={(event) => selecionarModelo(event.target.value)} disabled={modelos.isLoading} fullWidth>
      {(modelos.data ?? []).map((item: CadastroModelo) => <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>)}
    </TextField>
    {modelo && <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography fontWeight={700}>{cadastroEmEdicao ? t('fluxo.registrations.editRegistration', { name: modelo.nome }) : cadastroSendoDuplicado ? t('fluxo.registrations.duplicateRegistration', { name: modelo.nome }) : t('fluxo.registrations.newRegistration', { name: modelo.nome })}</Typography>
        <TextField label={t('fluxo.registrations.registrationName')} value={nome} onChange={(event) => setNome(event.target.value)} required fullWidth />
        {modelo.campos.map((campo) => {
          const opcoes = campo.opcoes ?? [];
          const select = campo.tipo === 'boolean' || opcoes.length > 0;
          return <TextField
            key={campo.nome}
            select={select}
            type={!select && campo.tipo === 'number' ? 'number' : !select && campo.tipo === 'date' ? 'date' : 'text'}
            label={`${campo.nome}${campo.identificador ? ` (${t('fluxo.registrations.identifier')})` : ''}`}
            value={valores[campo.nome] ?? ''}
            onChange={(event) => setValores((anterior) => ({ ...anterior, [campo.nome]: event.target.value }))}
            required={campo.obrigatorio}
            disabled={Boolean(cadastroEmEdicao && campo.identificador)}
            helperText={cadastroEmEdicao && campo.identificador ? t('fluxo.registrations.identifierImmutable') : campo.tipo === 'multiple' ? t('fluxo.registrations.commaSeparatedValues') : undefined}
            InputLabelProps={campo.tipo === 'date' ? { shrink: true } : undefined}
            fullWidth
          >
            {campo.tipo === 'boolean' && [<MenuItem key="true" value="true">{t('yes')}</MenuItem>, <MenuItem key="false" value="false">{t('no')}</MenuItem>]}
            {campo.tipo !== 'boolean' && opcoes.map((opcao) => <MenuItem key={opcao} value={opcao}>{opcao}</MenuItem>)}
          </TextField>;
        })}
        {duplicacaoMantemIdentificador && <Alert severity="warning">{t('fluxo.registrations.changeIdentifierToDuplicate')}</Alert>}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {(cadastroEmEdicao || cadastroSendoDuplicado) && <Button onClick={cancelarEdicao}>{t('cancel')}</Button>}
          <Button variant="contained" onClick={() => salvarCadastro.mutate()} disabled={!nome.trim() || !camposObrigatoriosPreenchidos || duplicacaoMantemIdentificador || salvarCadastro.isPending}>{cadastroEmEdicao ? t('fluxo.registrations.saveChanges') : cadastroSendoDuplicado ? t('fluxo.registrations.createCopy') : t('fluxo.registrations.saveRegistration')}</Button>
        </Stack>
      </Stack>
    </Card>}
    {cadastros.isError && <Alert severity="error">{t('fluxo.registrations.loadRegistrationsError')}</Alert>}
    {modelo && <Stack spacing={1}>
      <Typography fontWeight={700}>{t('fluxo.registrations.existingRegistrations')}</Typography>
      {Boolean(cadastros.data?.length) && <TextField
        label={t('fluxo.registrations.searchRegistrations')}
        value={pesquisaCadastros}
        onChange={(event) => {
          setPesquisaCadastros(event.target.value);
          setPagina(1);
        }}
        placeholder={t('fluxo.registrations.searchPlaceholder')}
        fullWidth
      />}
      {cadastros.isLoading && <Typography color="textSecondary">{t('loading')}</Typography>}
      {!cadastros.isLoading && !(cadastros.data?.length) && <Typography color="textSecondary">{t('fluxo.registrations.noRegistrationsFound')}</Typography>}
      {!cadastros.isLoading && Boolean(cadastros.data?.length) && !cadastrosFiltrados.length && <Typography color="textSecondary">{t('fluxo.registrations.noSearchResults')}</Typography>}
      {cadastrosDaPagina.map((cadastro) => <Card key={cadastro.id} variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack>
            <Typography fontWeight={600}>{cadastro.nome}</Typography>
            <Typography variant="body2" color="textSecondary">{cadastro.identificador ?? cadastro.identificadorNormalizado ?? t('fluxo.registrations.noIdentifier')}</Typography>
          </Stack>
          <IconButton aria-label={t('fluxo.registrations.actionsFor', { name: cadastro.nome })} onClick={(event) => setMenuCadastro({ ancora: event.currentTarget, cadastro })}>
            <MoreVertIcon />
          </IconButton>
        </Stack>
      </Card>)}
      {totalPaginas > 1 && <Pagination count={totalPaginas} page={pagina} onChange={(_event, novaPagina) => setPagina(novaPagina)} color="secondary" />}
    </Stack>}
    <Menu anchorEl={menuCadastro?.ancora} open={Boolean(menuCadastro)} onClose={fecharMenuCadastro}>
      <MenuItem onClick={() => {
        if (menuCadastro) editarCadastro(menuCadastro.cadastro);
        fecharMenuCadastro();
      }}>{t('edit')}</MenuItem>
      <MenuItem onClick={() => {
        if (menuCadastro) duplicarCadastro(menuCadastro.cadastro);
        fecharMenuCadastro();
      }}>{t('fluxo.registrations.duplicate')}</MenuItem>
      <MenuItem onClick={() => {
        if (menuCadastro) setCadastroParaExcluir(menuCadastro.cadastro);
        fecharMenuCadastro();
      }} sx={{ color: 'error.main' }}>{t('delete')}</MenuItem>
    </Menu>
    <Dialog open={modalAgendamentoAberto} onClose={fecharModalAgendamento} fullWidth maxWidth="sm">
      <DialogTitle>{t('fluxo.registrations.scheduling')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} paddingTop={1}>
          <Typography color="textSecondary">{t('fluxo.registrations.schedulingDescription')}</Typography>
          {modelosAgendamento.isError && <Alert severity="error">{t('fluxo.registrations.loadSchedulingModelsError')}</Alert>}
          {estrategiaIdAgendamento.isError && <Alert severity="error">{t('fluxo.registrations.loadSchedulingStrategyError')}</Alert>}
          <TextField select label={t('fluxo.registrations.schedulingModel')} value={modeloAgendamentoId} onChange={(event) => selecionarModeloAgendamento(event.target.value)} disabled={modelosAgendamento.isLoading} fullWidth>
            {(modelosAgendamento.data ?? []).map((item: ModeloAgendamento) => <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>)}
          </TextField>
          {fluxos.isError && <Alert severity="error">{t('fluxo.registrations.loadFlowModelsError')}</Alert>}
          <TextField select label={t('fluxo.registrations.flowModel')} value={fluxoId} onChange={(event) => selecionarFluxo(event.target.value)} disabled={fluxos.isLoading} fullWidth>
            {(fluxos.data ?? []).filter((item) => !item.dataDesativacao).map((item) => <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>)}
          </TextField>
          {unidades.isError && <Alert severity="error">{t('fluxo.registrations.loadFlowUnitsError')}</Alert>}
          <TextField select label={t('fluxo.registrations.unit')} value={unidadeId} onChange={(event) => setUnidadeId(event.target.value)} disabled={!fluxoId || unidades.isLoading} fullWidth>
            {(unidades.data ?? []).map((item: { unidadeId: string; unidadeNome: string }) => <MenuItem key={item.unidadeId} value={item.unidadeId}>{item.unidadeNome}</MenuItem>)}
          </TextField>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DesktopDateTimePicker
              label={t('fluxo.registrations.expectedDateTime')}
              format="dd/MM/yyyy HH:mm"
              minDateTime={dataMinimaAgendamento ?? new Date()}
              value={dataPrevista}
              onChange={setDataPrevista}
              slotProps={{ actionBar: { actions: ['clear'] }, textField: { required: true, fullWidth: true } }}
            />
          </LocalizationProvider>
          {modeloAgendamento && <>
            {camposIdentificadorAgendamento.map((campo) => <TextField
              key={campo}
              label={labelDoCampoIdAgendamento(campo)}
              value={valoresIdentificadorAgendamento[campo] ?? ''}
              onChange={(event) => setValoresIdentificadorAgendamento((valores) => ({ ...valores, [campo]: event.target.value }))}
              required
              disabled={estrategiaIdAgendamento.isLoading || criarAgendamento.isPending}
              fullWidth
            />)}
            {regrasAgendamento.map((regra, indice) => {
              const consulta = cadastrosDasRegras[indice];
              const opcoes = consulta?.data ?? [];
              const selecionados = opcoes.filter((item) => (cadastrosPorRegra[regra.id] ?? []).includes(item.id));
              return <GenericAutoComplete
                key={regra.id}
                options={opcoes.map((item) => ({
                  label: `${item.nome} — ${item.identificador ?? item.identificadorNormalizado ?? t('fluxo.registrations.noIdentifier')}`,
                  value: item.id,
                }))}
                selectedValues={selecionados.map((item) => item.id)}
                loading={consulta?.isLoading}
                onChange={(novosValores) => setCadastrosPorRegra((anterior) => ({ ...anterior, [regra.id]: novosValores }))}
                label={regra.nome}
                helperText={instrucaoDaRegra(regra.minimo, regra.maximo)}
                maxSelections={regra.maximo ?? undefined}
              />;
            })}
          </>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={fecharModalAgendamento}>{t('cancel')}</Button>
        <Button variant="contained" onClick={() => criarAgendamento.mutate()} disabled={!modeloAgendamento || !fluxoId || !checklistFluxoId || !unidadeId || !dataPrevista || dataPrevistaInvalida || !identificadorAgendamentoPreenchido || estrategiaIdAgendamento.isLoading || estrategiaIdAgendamento.isError || !regrasAtendidas || criarAgendamento.isPending}>{t('fluxo.registrations.createScheduling')}</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(cadastroParaExcluir)} onClose={() => setCadastroParaExcluir(null)}>
      <DialogTitle>{t('fluxo.registrations.deleteRegistration')}</DialogTitle>
      <DialogContent>
        <Typography>{t('fluxo.registrations.deleteConfirmation', { name: cadastroParaExcluir?.nome })}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCadastroParaExcluir(null)} disabled={excluirCadastro.isPending}>{t('cancel')}</Button>
        <Button color="error" variant="contained" onClick={() => cadastroParaExcluir && excluirCadastro.mutate(cadastroParaExcluir)} disabled={excluirCadastro.isPending}>{t('delete')}</Button>
      </DialogActions>
    </Dialog>
  </Stack>;
}
